import { Button, FormContainer, Flex, Text } from '@wyz/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { SelectField, TextField } from '~/common/components';
import { Trans, useI18n } from '~/common/context';
import { useValidationForm } from '~/common/hooks';
import { IS2SBusinessRuleForm } from '~/connect/types';
import {
  s2sBusinessRuleFormSchema,
  S2S_BUSINESS_RULE_CONSTRAINT_TYPES,
  S2S_BUSINESS_RULE_PARAMETER_TYPES,
} from '~/connect/schemas';
import { IErrorApiResponse, IValidationError } from '@wyz/types';
import { CommonErrorCodesEnumType } from '~/common/types';

type Props = {
  mode: 'UPDATE' | 'READ_ONLY';
  defaultValues: IS2SBusinessRuleForm;
  onSubmit?: (data: IS2SBusinessRuleForm) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  errors?: IErrorApiResponse<
    IValidationError<CommonErrorCodesEnumType>,
    CommonErrorCodesEnumType
  > | null;
};

type ConstraintOptionsProps = {
  paramIndex: number;
  constraintIndex: number;
  isReadOnly: boolean;
};

function ConstraintOptions({
  paramIndex,
  constraintIndex,
  isReadOnly,
}: ConstraintOptionsProps) {
  const { control } = useFormContext<IS2SBusinessRuleForm>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `parameterDefinitions.${paramIndex}.constraints.${constraintIndex}.options`,
  });

  return (
    <Flex direction='column' className='border-start border-2 ps-3 mt-2'>
      {fields.map((optionField, i) => (
        <Flex
          key={optionField.id}
          direction='row'
          alignItems='end'
          wrap='wrap'
          className='gap-2 mb-2'
        >
          <Flex direction='column' className='flex-grow-1'>
            <TextField
              name={`parameterDefinitions.${paramIndex}.constraints.${constraintIndex}.options.${i}.key`}
              label={
                <Trans i18nKey='app.s2s_business_rule.form.parameter_definitions.constraints.key' />
              }
              readOnly={isReadOnly}
            />
          </Flex>
          <Flex direction='column' className='flex-grow-1'>
            <TextField
              name={`parameterDefinitions.${paramIndex}.constraints.${constraintIndex}.options.${i}.value`}
              label={
                <Trans i18nKey='app.s2s_business_rule.form.parameter_definitions.constraints.value' />
              }
              readOnly={isReadOnly}
            />
          </Flex>
          {!isReadOnly && (
            <Button
              id={`remove-option-${paramIndex}-${constraintIndex}-${i}`}
              variant='link'
              size='sm'
              type='button'
              color='danger'
              onClick={() => remove(i)}
            >
              <Trans i18nKey='app.s2s_business_rule.form.parameter_definitions.constraints.remove' />
            </Button>
          )}
        </Flex>
      ))}
      {!isReadOnly && (
        <Button
          id={`add-option-${paramIndex}-${constraintIndex}`}
          variant='secondary'
          size='sm'
          type='button'
          color='green'
          className='align-self-start'
          onClick={() => append({ key: '', value: '' })}
        >
          <Trans i18nKey='app.s2s_business_rule.form.parameter_definitions.constraints.add' />
        </Button>
      )}
    </Flex>
  );
}

type ParameterConstraintsProps = {
  index: number;
  isReadOnly: boolean;
};

function ParameterConstraints({
  index,
  isReadOnly,
}: ParameterConstraintsProps) {
  const { control } = useFormContext<IS2SBusinessRuleForm>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `parameterDefinitions.${index}.constraints`,
  });

  return (
    <Flex direction='column' className='gap-3 mt-2'>
      {fields.map((constraintField, i) => (
        <Flex
          key={constraintField.id}
          direction='column'
          className='border rounded p-3 bg-light'
        >
          <Flex direction='row' alignItems='end' className='gap-2'>
            <Flex direction='column' className='flex-grow-1'>
              <SelectField
                name={`parameterDefinitions.${index}.constraints.${i}.type`}
                label={
                  <Trans i18nKey='app.s2s_business_rule.form.parameter_definitions.constraints.type' />
                }
                options={S2S_BUSINESS_RULE_CONSTRAINT_TYPES.map((v) => ({
                  value: v,
                  label: v,
                }))}
                isDisabled={isReadOnly}
                formGroupClassName=''
              />
            </Flex>
            {!isReadOnly && (
              <Button
                id={`remove-constraint-${index}-${i}`}
                variant='link'
                size='sm'
                type='button'
                color='danger'
                onClick={() => remove(i)}
              >
                <Trans i18nKey='app.s2s_business_rule.form.parameter_definitions.constraints.remove' />
              </Button>
            )}
          </Flex>
          <ConstraintOptions
            paramIndex={index}
            constraintIndex={i}
            isReadOnly={isReadOnly}
          />
        </Flex>
      ))}
      {!isReadOnly && (
        <Button
          id={`add-constraint-${index}`}
          variant='secondary'
          size='sm'
          type='button'
          color='green'
          className='align-self-start'
          onClick={() => append({ type: 'NOT_BLANK', options: [] })}
        >
          <Trans i18nKey='app.s2s_business_rule.form.parameter_definitions.constraints.add' />
        </Button>
      )}
    </Flex>
  );
}

export function S2SBusinessRuleForm({
  mode,
  defaultValues,
  onSubmit,
  onCancel,
  isLoading,
  errors,
}: Props) {
  const { t } = useI18n();
  const isReadOnly = mode === 'READ_ONLY';

  const methods = useForm<IS2SBusinessRuleForm>({
    defaultValues,
    resolver: zodResolver(s2sBusinessRuleFormSchema),
  });

  useValidationForm({ methods, errors: errors?.errors, t });

  const { fields } = useFieldArray({
    control: methods.control,
    name: 'parameterDefinitions',
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={(data) => onSubmit?.(data)}
      formConfiguration={{ mode: isReadOnly ? 'READ_ONLY' : 'WRITE' }}
    >
      <TextField
        name='code'
        label={<Trans i18nKey='app.s2s_business_rule.form.code' />}
        readOnly
      />
      <TextField
        name='name.defaultValue'
        label={
          <Trans i18nKey='app.s2s_business_rule.form.name.default_value' />
        }
        readOnly={isReadOnly}
      />
      <TextField
        name='name.i18nKey'
        label={<Trans i18nKey='app.s2s_business_rule.form.name.i18n_key' />}
        readOnly={isReadOnly}
      />
      <TextField
        name='description.defaultValue'
        label={
          <Trans i18nKey='app.s2s_business_rule.form.description.default_value' />
        }
        readOnly={isReadOnly}
      />
      <TextField
        name='description.i18nKey'
        label={
          <Trans i18nKey='app.s2s_business_rule.form.description.i18n_key' />
        }
        readOnly={isReadOnly}
      />

      {fields.length === 0 ? (
        <Text heading='sm' color='secondary-gray'>
          <Trans i18nKey='app.s2s_business_rule.form.no_parameters' />
        </Text>
      ) : (
        fields.map((field, index) => (
          <Flex key={field.id} direction='column' className='mb-4'>
            <TextField
              name={`parameterDefinitions.${index}.key`}
              label={
                <Trans i18nKey='app.s2s_business_rule.form.parameter_definitions.key' />
              }
              readOnly
            />
            <TextField
              name={`parameterDefinitions.${index}.i18nKey`}
              label={
                <Trans i18nKey='app.s2s_business_rule.form.parameter_definitions.i18n_key' />
              }
              readOnly={isReadOnly}
            />
            <SelectField
              name={`parameterDefinitions.${index}.type`}
              label={
                <Trans i18nKey='app.s2s_business_rule.form.parameter_definitions.type' />
              }
              options={S2S_BUSINESS_RULE_PARAMETER_TYPES.map((v) => ({
                value: v,
                label: v,
              }))}
              isDisabled={isReadOnly}
            />
            <TextField
              name={`parameterDefinitions.${index}.defaultValue`}
              label={
                <Trans i18nKey='app.s2s_business_rule.form.parameter_definitions.default_value' />
              }
              readOnly={isReadOnly}
            />
            <ParameterConstraints index={index} isReadOnly={isReadOnly} />
          </Flex>
        ))
      )}

      {!isReadOnly && (
        <Flex justifyContent='end' className='gap-2 mt-3'>
          {onCancel && (
            <Button
              id='cancel'
              type='button'
              variant='link'
              disabled={isLoading}
              onClick={onCancel}
            >
              <Trans i18nKey='app.common.button.cancel' />
            </Button>
          )}
          <Button
            id={mode}
            type='submit'
            variant='primary'
            disabled={isLoading}
          >
            <Trans i18nKey='app.common.button.save' />
          </Button>
        </Flex>
      )}
    </FormContainer>
  );
}
